import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { packageFeatures } from '../mock';
import { CheckCircle, ArrowRight, Sparkles } from 'lucide-react';
import { PageSEO } from '../components/SEO';

const Komplettpaket = () => {
  const premiumFeatures = [
    'Alle Leistungen des Basis-Pakets',
    'Individueller Gesellschaftsvertrag',
    'Geschäftsführervertrag',
    'IHK-Anmeldung',
    'Finanzamt-Anmeldung',
    'Berufsgenossenschaft-Anmeldung',
    'Unterstützung bei Kontoeröffnung',
  ];

  const pricingTiers = [
    {
      name: 'Basis-Paket',
      price: '2.500',
      description: 'Ideal für einfache Gründungen',
      features: [
        'Kostenlose Erstberatung',
        'Gesellschaftsvertrag (Musterprotokoll)',
        'Notar-Terminvereinbarung',
        'Handelsregister-Anmeldung',
        'Gewerbeanmeldung',
      ],
    },
    {
      name: 'Premium-Paket',
      price: '3.900',
      description: 'Die beliebteste Wahl unserer Kunden',
      features: premiumFeatures,
      popular: true,
      badgeLabel: 'Beliebteste Wahl',
    },
    {
      name: 'Autovermietung-Spezialpaket',
      price: '9.500',
      pricePrefix: 'Kaufpreis',
      priceFootnote: 'zzgl. MwSt.',
      description: 'Wie Premium – plus Stammkapital-Lösung für 250.000 €',
      features: [...premiumFeatures, 'Bereitstellung Stammkapital 250.000 € (über externe Lösung)'],
      popular: true,
      badgeLabel: 'Spezialpaket',
    },
    {
      name: 'Business-Paket',
      price: '5.500',
      description: 'Rundum-Sorglos für anspruchsvolle Gründungen',
      features: [
        'Alle Leistungen des Premium-Pakets',
        'Komplexe Gesellschafterstrukturen',
        'Begleitung zum Notar',
        '12 Monate Nachbetreuung',
        'Steuerliche Erstberatung',
        'Virtuelle Geschäftsadresse (6 Monate)',
        'Prioritäts-Support',
      ],
    },
  ];

  return (
    <>
      <PageSEO 
        title="GmbH Komplettpaket – Firmengründung zum Festpreis | Firmbay"
        description="GmbH gründen zum Festpreis mit dem Firmbay Komplettpaket. Von der Beratung bis zur Eintragung – alles aus einer Hand."
        path="/komplettpaket"
      />
      <div className="min-h-screen">
      {/* Hero Section */}
      <section className="pt-40 pb-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-blue-50 to-white">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Komplettpaket
            <span className="block mt-2 bg-gradient-to-r from-blue-600 to-teal-600 bg-clip-text text-transparent">
              Firmengründung
            </span>
          </h1>
          <p className="text-lg text-gray-600 mb-8 leading-relaxed max-w-3xl mx-auto">
            Alles aus einer Hand – von der ersten Beratung bis zur vollständigen Eintragung deiner GmbH. 
            Wähle das Paket, das zu deinen Bedürfnissen passt.
          </p>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {pricingTiers.map((tier, index) => (
              <Card
                key={index}
                className={`relative border-2 transition-all hover:shadow-xl ${
                  tier.popular
                    ? 'border-blue-600 shadow-lg transform md:scale-105'
                    : 'border-gray-200 hover:border-blue-300'
                }`}
              >
                {tier.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <div className="bg-gradient-to-r from-blue-600 to-teal-600 text-white px-4 py-1 rounded-full text-sm font-semibold flex items-center space-x-1 shadow-md">
                      <Sparkles className="w-4 h-4" />
                      <span>{tier.badgeLabel || 'Beliebteste Wahl'}</span>
                    </div>
                  </div>
                )}
                <CardHeader className="text-center pb-8 pt-8">
                  <CardTitle className="text-2xl font-bold text-gray-900 mb-2">
                    {tier.name}
                  </CardTitle>
                  <p className="text-gray-600 mb-6">{tier.description}</p>
                  {tier.pricePrefix && (
                    <p className="text-sm font-medium text-gray-700 mb-2">
                      {tier.pricePrefix}
                    </p>
                  )}
                  <div className="mb-2">
                    <span className="text-5xl font-bold text-gray-900">
                      {tier.price}
                    </span>
                    <span className="text-xl text-gray-600 ml-1">€</span>
                  </div>
                  <p className="text-sm text-gray-500">{tier.priceFootnote || 'einmalig zzgl. MwSt.'}</p>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3 mb-8">
                    {tier.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start space-x-3">
                        <CheckCircle className="w-5 h-5 text-teal-500 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700 text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <Link to="/kontakt">
                    <Button
                      className={`w-full ${
                        tier.popular
                          ? 'bg-gradient-to-r from-blue-600 to-teal-600 hover:from-blue-700 hover:to-teal-700 text-white shadow-md'
                          : 'bg-gray-900 hover:bg-gray-800 text-white'
                      }`}
                    >
                      Paket auswählen
                      <ArrowRight className="ml-2 w-4 h-4" />
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* All Features Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 text-center">
            Alle enthaltenen Leistungen
          </h2>
          <p className="text-lg text-gray-600 mb-12 text-center">
            Im Premium-Paket sind alle wichtigen Schritte für deine erfolgreiche GmbH-Gründung enthalten
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {packageFeatures.map((feature, index) => (
              <Card key={index} className="border-none shadow-sm hover:shadow-md transition-shadow">
                <CardContent className="pt-4 pb-4">
                  <div className="flex items-center space-x-3">
                    <CheckCircle className="w-5 h-5 text-teal-500 flex-shrink-0" />
                    <span className="text-gray-700">{feature}</span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Process Timeline */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-12 text-center">
            So läuft deine Gründung ab
          </h2>
          <div className="space-y-8">
            {[
              {
                week: 'Woche 1',
                title: 'Beratung & Vorbereitung',
                description: 'Persönliche Erstberatung, Klärung aller Fragen und Erstellung der Gründungsunterlagen.',
              },
              {
                week: 'Woche 2',
                title: 'Notartermin & Kontoeröffnung',
                description: 'Beurkundung des Gesellschaftsvertrags beim Notar und Eröffnung des Geschäftskontos.',
              },
              {
                week: 'Woche 3-4',
                title: 'Eintragung & Anmeldungen',
                description: 'Handelsregistereintrag, Gewerbeanmeldung und alle weiteren behördlichen Anmeldungen.',
              },
            ].map((phase, index) => (
              <div key={index} className="flex items-start space-x-6">
                <div className="flex-shrink-0">
                  <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-teal-500 rounded-full flex items-center justify-center text-white font-bold shadow-lg">
                    {index + 1}
                  </div>
                </div>
                <div className="flex-1">
                  <div className="text-sm font-semibold text-blue-600 mb-1">{phase.week}</div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{phase.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{phase.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-blue-600 to-teal-600">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Starte jetzt mit deinem Paket
          </h2>
          <p className="text-xl text-blue-50 mb-10">
            Kontaktiere uns für eine unverbindliche Beratung und ein individuelles Angebot.
          </p>
          <Link to="/kontakt">
            <Button size="lg" className="bg-white text-blue-600 hover:bg-gray-100 shadow-xl text-base px-8">
              Jetzt Beratung vereinbaren
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
    </>
  );
};

export default Komplettpaket;